import React, { useState } from "react";
import { BULK_PRODUCT_UPLOAD } from "../../../hooks/APIHooks";
import {
  FaUpload,
  FaDownload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileCsv,
} from "react-icons/fa";

const UploadCSV = () => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage("");
    setErrors([]);
    setIsSuccess(false);

    // Validate file type
    if (selectedFile && !selectedFile.name.endsWith(".csv")) {
      setMessage("Please select a CSV file.");
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file first.");
      return;
    }

    setIsUploading(true);
    setMessage("");
    setErrors([]);
    setIsSuccess(false);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(BULK_PRODUCT_UPLOAD, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.errors && data.errors.length > 0) {
        setErrors(data.errors);
        setMessage(data.message || "Some products could not be uploaded.");
        setIsSuccess(false);
      } else {
        setMessage(data.message || "CSV data successfully imported!");
        setErrors([]);
        setIsSuccess(true);
      }
    } catch (error) {
      setMessage("Error uploading file. Please try again.");
      setIsSuccess(false);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `product_type,sub_catalogue_name,sku,product_name,product_description,price,sale_price,stock_quantity,weight_kg,length_cm,width_cm,height_cm,option_name
1,single,Test Sub-cat,CSD0000000001,Test Product,Test Description,100.00,80.00,50,1.5,30,20,10,
1,multivariate,Test Sub-cat,CSD0000000002,Multivariate Product,Product with variants,0.00,0.00,0,1.5,30,20,10,Red
1,multivariate,Test Sub-cat,CSD0000000002,Multivariate Product,Product with variants,120.00,100.00,25,1.5,30,20,10,Blue
1,single,Test Sub-cat,CSD0000000003,Another Product,Another description,150.00,120.00,75,2.0,35,25,15,`;

    const blob = new Blob([sampleData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "product_upload_sample.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center items-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <FaFileCsv className="text-3xl text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Bulk Product Upload
        </h2>
        <p className="text-gray-600">
          Upload a CSV file to add multiple products at once
        </p>
      </div>

      {/* Download Sample Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-blue-800 mb-1">
              Need a template?
            </h3>
            <p className="text-blue-600 text-sm">
              Download our sample CSV file to get started
            </p>
          </div>
          <button
            onClick={downloadSampleCSV}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <FaDownload />
            Download Sample
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          file
            ? "border-green-400 bg-green-50"
            : "border-gray-300 bg-gray-50 hover:border-blue-400"
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
          id="csv-upload"
        />

        {!file ? (
          <div className="space-y-4">
            <FaUpload className="text-4xl text-gray-400 mx-auto" />
            <div>
              <p className="text-gray-600 mb-2">
                Drag & drop your CSV file here
              </p>
              <p className="text-gray-500 text-sm mb-4">or</p>
            </div>
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer transition-colors"
            >
              <FaUpload />
              Choose CSV File
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <FaFileCsv className="text-4xl text-green-500 mx-auto" />
            <div>
              <p className="font-semibold text-green-700">{file.name}</p>
              <p className="text-gray-600 text-sm">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload />
                    Upload Now
                  </>
                )}
              </button>
              <button
                onClick={() => setFile(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Change File
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-3">
          CSV Format Requirements:
        </h3>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>File must be in CSV format with proper headers</li>
          <li>First row should contain column headers</li>
          <li>
            Required columns: product_type, sub_catalogue_name, sku,
            product_name
          </li>
          <li>Product type must be 'single' or 'multivariate'</li>
          <li>For multivariate products, include option_name for variants</li>
        </ul>
      </div>

      {/* Messages */}
      {message && (
        <div
          className={`mt-6 p-4 rounded-lg border ${
            isSuccess
              ? "bg-green-50 border-green-200 text-green-800"
              : errors.length > 0
              ? "bg-yellow-50 border-yellow-200 text-yellow-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <div className="flex items-start gap-3">
            {isSuccess ? (
              <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
            ) : (
              <FaExclamationTriangle className="text-yellow-500 mt-1 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="font-medium">{message}</p>

              {/* Display Errors */}
              {errors.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="font-medium text-sm">Detailed Errors:</p>
                  <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-500 mt-1">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadCSV;
